import {
  type BacklogView,
  type BoardView,
  type BoardWindowQuery,
  type CreateSprintInput,
  type CreateStatusInput,
  type ProjectView,
  type SprintView,
  type TaskMutationResult,
  type TaskView,
  type UpdateStatusInput,
  type WorkflowStatusView,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export const boardApi = {
  window: (
    projectId: string,
    sprintId: string | undefined,
    all: boolean,
    period: string | null | undefined,
    query: Partial<BoardWindowQuery>,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (sprintId) params.set('sprintId', sprintId);
    if (all) params.set('all', '1');
    if (period) params.set('period', period);
    for (const [key, value] of Object.entries(query))
      if (value !== undefined)
        params.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    return apiFetch<BoardView>(`/projects/${projectId}/board/window?${params}`, {
      ...(signal ? { signal } : {}),
    });
  },
  projects: () => apiFetch<ProjectView[]>('/projects'),

  /** `period`: kỳ của dự án bảo trì (APM-MAINT-e). Bỏ trống = không lọc thời gian. */
  board: (
    projectId: string,
    sprintId?: string,
    all = false,
    period?: string | null,
    metadata = false,
  ) => {
    const params = new URLSearchParams();
    if (sprintId) params.set('sprintId', sprintId);
    if (metadata) params.set('metadata', '1');
    if (all) params.set('all', '1');
    if (period) params.set('period', period);
    const query = params.toString();
    return apiFetch<BoardView>(`/projects/${projectId}/board${query ? `?${query}` : ''}`);
  },

  /** `period`: kỳ của dự án bảo trì (APM-MAINT-h). Bỏ trống = không lọc thời gian. */
  backlog: (
    projectId: string,
    period?: string | null,
    metadata = false,
    page?: Partial<BoardWindowQuery>,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (metadata) params.set('metadata', '1');
    if (page)
      for (const [key, value] of Object.entries(page))
        if (value !== undefined && typeof value !== 'object') params.set(key, String(value));
    return apiFetch<BacklogView>(
      `/projects/${projectId}/backlog${page ? '/window' : ''}?${params}`,
      { ...(signal ? { signal } : {}) },
    );
  },

  /**
   * Một task theo id, không phụ thuộc board đang hiện gì.
   *
   * Dùng cho deep-link từ thông báo (APM-NOTI-c): board của dự án Agile chỉ
   * chứa sprint đang chạy cộng backlog, nên một task ở sprint đã đóng không nằm
   * trong đó — mà thông báo về nó thì vẫn phải mở được.
   */
  task: (taskId: string) => apiFetch<TaskView>(`/tasks/${taskId}`),

  /**
   * Kéo thẻ sang cột khác. Trả kèm `warnings[]` khi cột đích vượt WIP limit.
   * `actualStartAt` = giờ bắt đầu thực tế khai lại ở hộp "chưa đủ thời gian
   * tối thiểu" (APM-TMIN-b), gửi cùng lần chuyển.
   */
  changeStatus: (
    taskId: string,
    statusId: string,
    position: number,
    actualStartAt?: string,
    /** Nút "Bắt đầu làm" nhanh: việc ở sprint chưa chạy sang sprint đang chạy (2026-10-30). */
    intoRunningSprint?: boolean,
  ) =>
    apiFetch<TaskMutationResult>(`/tasks/${taskId}/status`, {
      method: 'PATCH',
      body: {
        statusId,
        position,
        ...(actualStartAt ? { actualStartAt } : {}),
        ...(intoRunningSprint ? { intoRunningSprint } : {}),
      },
    }),

  /** Kéo thả trong cùng cột. */
  reorder: (taskId: string, position: number) =>
    apiFetch<TaskView>(`/tasks/${taskId}/rank`, { method: 'PATCH', body: { position } }),

  moveToSprint: (taskId: string, sprintId: string | null, position?: number) =>
    apiFetch<TaskView>(`/tasks/${taskId}/sprint`, {
      method: 'PATCH',
      body: { sprintId, position },
    }),

  /** `actualStartAt` khi kéo lên 100% sau khi khai lại giờ bắt đầu — APM-TMIN-c. */
  setProgress: (taskId: string, progressPct: number, actualStartAt?: string) =>
    apiFetch<TaskView>(`/tasks/${taskId}/progress`, {
      method: 'PATCH',
      body: { progressPct, ...(actualStartAt ? { actualStartAt } : {}) },
    }),

  /** Tích / bỏ tích một mục checklist — `%` do server tính lại từ các mục đã tích. */
  toggleChecklist: (taskId: string, itemId: string, checked: boolean) =>
    apiFetch<TaskView>(`/tasks/${taskId}/checklist/${itemId}`, {
      method: 'PUT',
      body: { checked },
    }),

  createSprint: (projectId: string, input: CreateSprintInput) =>
    apiFetch<SprintView>(`/projects/${projectId}/sprints`, { method: 'POST', body: input }),

  startSprint: (sprintId: string) =>
    apiFetch<SprintView>(`/sprints/${sprintId}/start`, { method: 'POST' }),

  closeSprint: (sprintId: string, carryOver: 'backlog' | 'next_sprint') =>
    apiFetch<SprintView & { carriedOver: number; completed: number }>(
      `/sprints/${sprintId}/close`,
      { method: 'POST', body: { carryOver } },
    ),

  myTasks: () => apiFetch<TaskView[]>('/me/tasks'),

  // ── Cột board tuỳ biến (spec mục 1) ───────────────────────────────────────
  //
  // Cả bốn endpoint trả về **danh sách cột sau khi sửa**, nên client không phải
  // tải lại dự án để biết kết quả — trừ `delete`, trả 204.

  createStatus: (projectId: string, input: CreateStatusInput) =>
    apiFetch<WorkflowStatusView[]>(`/projects/${projectId}/statuses`, {
      method: 'POST',
      body: input,
    }),

  updateStatus: (projectId: string, statusId: string, input: UpdateStatusInput) =>
    apiFetch<WorkflowStatusView[]>(`/projects/${projectId}/statuses/${statusId}`, {
      method: 'PATCH',
      body: input,
    }),

  reorderStatuses: (projectId: string, statusIds: string[]) =>
    apiFetch<WorkflowStatusView[]>(`/projects/${projectId}/statuses`, {
      method: 'PATCH',
      body: { statusIds },
    }),

  /**
   * Kéo cột vào khung sprint hoặc ra ngoài khung — APM-BOARD-b.
   *
   * Trả kèm `displacedTaskCount` vì đây là thao tác đụng tới task: giao diện đã
   * cảnh báo trước khi gọi, nên nó cần con số thật để báo lại.
   */
  moveStatus: (projectId: string, statusId: string, sprintId: string | null) =>
    apiFetch<{ statuses: WorkflowStatusView[]; displacedTaskCount: number }>(
      `/projects/${projectId}/statuses/${statusId}/sprint`,
      { method: 'PATCH', body: { sprintId } },
    ),

  deleteStatus: (projectId: string, statusId: string) =>
    apiFetch<void>(`/projects/${projectId}/statuses/${statusId}`, { method: 'DELETE' }),
};

export const boardKeys = {
  projects: () => ['projects'] as const,
  board: (projectId: string, sprintId?: string, period?: string | null) =>
    // Kỳ nằm **trong khoá**: đổi kỳ là đổi tập việc, nên hai kỳ không được dùng
    // chung một ô cache — cùng lý do với sprint (xem `ganttKeys.view`).
    ['board', projectId, sprintId ?? null, period ?? null] as const,
  backlog: (projectId: string, period?: string | null) =>
    ['backlog', projectId, period ?? null] as const,
  task: (taskId: string) => ['task', taskId] as const,
  myTasks: () => ['me', 'tasks'] as const,
};
